<template>
    <lti-drawer
        :active="active"
        :title="drawerTitle"
        :maximized="isDrawerMaximized"
        style="--lti-sidebar-width: 75%; --lti-sidebar-maxwidth: 75vw"
        @close="$emit('close')"
        @update:active="handleUpdateActive"
        @restore="handleRestore"
        @state="handleMaximizedChange"
    >
        <div class="scorm-drawer">
            <!-- Sub-header: SCORM badges + Reset + runtime toggle -->
            <div class="scorm-subheader">
                <div class="scorm-subheader__left">
                    <span class="scorm-badge scorm-badge--standard tw:text-base">{{ pkg?.standard || 'SCORM 1.2' }}</span>
                    <span class="scorm-badge scorm-badge--version tw:text-base">{{ pkg?.version }}</span>

                    <!-- SCO selector (when multiple SCOs exist) -->
                    <div v-if="availableScos.length > 1" class="scorm-sco-selector">
                        <label class="scorm-sco-selector__label tw:text-base">SCO:</label>
                        <!-- eslint-disable-next-line vue-a11y/no-onchange -->
                        <select v-model="selectedScoId" class="scorm-sco-selector__select tw:text-sm" @change="onScoChange">
                            <option v-for="(sco, idx) in availableScos" :key="sco.id" :value="sco.id">
                                {{ idx + 1 }}. {{ sco.title }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="scorm-subheader__right">
                    <button
                        class="scorm-ctrl-btn tw:text-sm"
                        title="Reset attempt"
                        :disabled="resettingAttempt"
                        @click="resetAttempt"
                    >
                        <i class="pi" :class="resettingAttempt ? 'pi-spin pi-spinner' : 'pi-replay'" />
                        Reset
                    </button>
                </div>
            </div>

            <!-- Split body -->
            <div class="scorm-body">
                <!-- SCO iframe -->
                <div class="scorm-player">
                    <div v-if="previewLoading" class="scorm-player__loading">
                        <i class="pi pi-spin pi-spinner tw:text-2xl tw:text-gray-400" />
                        <p class="tw:mt-2 tw:text-sm tw:text-gray-500">Loading preview…</p>
                    </div>
                    <div v-else-if="previewError" class="scorm-player__loading scorm-player__error">
                        <i class="pi pi-exclamation-triangle tw:text-3xl tw:text-amber-500" />
                        <p class="tw:mt-3 tw:text-sm tw:text-gray-600">{{ previewError }}</p>
                        <button class="scorm-retry-btn tw:mt-4 tw:text-sm" @click="loadPreview">
                            <i class="pi pi-refresh" />
                            Retry
                        </button>
                    </div>
                    <div v-else-if="launchUrl && !iframeReady" class="scorm-player__loading">
                        <i class="pi pi-spin pi-spinner tw:text-2xl tw:text-gray-400" />
                        <p class="tw:mt-2 tw:text-sm tw:text-gray-500">Loading SCO…</p>
                    </div>
                    <iframe
                        v-if="launchUrl"
                        ref="scoFrame"
                        :key="iframeKey"
                        :src="launchUrl"
                        class="scorm-iframe"
                        :class="{ 'scorm-iframe--ready': iframeReady }"
                        title="E-learning package preview"
                        @load="onIframeLoad"
                    />
                    <div v-else class="scorm-player__loading">
                        <i class="pi pi-box tw:text-3xl tw:text-gray-300" />
                        <p class="tw:mt-3 tw:text-sm tw:text-gray-400">No launchable SCO found for this package.</p>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <e-button type="is-light" outlined class="decline-report-close-btn tw:mr-4" @click="close">
                <i class="pi pi-times tw:mr-1" aria-hidden="true" />
                Close
            </e-button>
        </template>
    </lti-drawer>
</template>

<script>
import LtiDrawer from '@/components/lti/LtiDrawer.vue';
import UserSetting from '@/models/UserSetting.js';
import { fetchPackageLaunch, fetchPackagePreview } from '@/utils/scorm12Api.js';

const LOAD_DEBOUNCE_MS = 50;

export default {
    name: 'ScormStudentDrawer',

    components: { LtiDrawer },

    props: {
        active: { type: Boolean, default: false },
        pkg: { type: Object, default: null },
        // Prefer hiding runtime for student by default
        hideRuntime: { type: Boolean, default: true },
        itemId: { type: [Number, String], default: null },
        activityId: { type: [Number, String], default: null },
        courseId: { type: [Number, String], default: null },
        // If provided, preview as this user id (server may log attempts)
        userId: { type: [Number, String], default: null },
        // When true (course activity only), persist drawer size between sessions
        rememberDrawerState: { type: Boolean, default: false },
    },

    emits: ['close', 'progress-synced'],

    data() {
        return {
            isDrawerMaximized: false,
            iframeReady: false,
            previewLoading: false,
            previewError: null,
            launchUrl: null,
            iframeKey: 0,
            loadToken: 0,
            selectedScoId: null,
            resettingAttempt: false,
            attemptId: null,
            _loadDebounceTimer: null,
            _progressPollTimer: null,
        };
    },

    computed: {
        drawerTitle() {
            return this.pkg?.title ?? 'SCORM Preview';
        },

        packageId() {
            return this.pkg?.id ?? null;
        },

        availableScos() {
            // Packages may include SCOs directly as `pkg.scos`, or nested on
            // `pkg.latest_version.scos` / `pkg.latestVersion.scos`, or the
            // package may expose `latest_version.items` which we can map to
            // a minimal SCO shape. Support all those shapes so the selector
            // appears regardless of which API shape the caller passed.
            const scos = this.pkg?.scos ?? this.pkg?.latest_version?.scos ?? this.pkg?.latestVersion?.scos;

            if (Array.isArray(scos) && scos.length) return scos;

            const items = this.pkg?.latest_version?.items ?? this.pkg?.latestVersion?.items ?? [];
            if (Array.isArray(items) && items.length) {
                return items.map(item => ({
                    id: item.id,
                    title: item.title || item.name || `Item ${item.id}`,
                    href: item.launch_path ?? item.href ?? null,
                    mastery_score: item.metadata?.mastery_score ?? item.mastery_score ?? null,
                }));
            }

            return [];
        },
    },

    watch: {
        // hideRuntime handled by prop; no runtime UI in student drawer

        active(val) {
            if (val) {
                this.syncDrawerStateOnOpen();
                this.initializeSelectedSco();
                this.debounceLoadPreview();
                this.startProgressPolling();
            } else {
                this.resetPreview();
                this.stopProgressPolling();
            }
        },

        packageId() {
            if (this.active) {
                this.initializeSelectedSco();
                this.debounceLoadPreview();
            }
        },

        itemId() {
            if (this.active && this.itemId) {
                this.selectedScoId = this.itemId;
                this.debounceLoadPreview();
            }
        },

        availableScos() {
            if (this.active && !this.selectedScoId) {
                this.initializeSelectedSco();
            }
        },
    },

    mounted() {
        if (this.active) {
            this.syncDrawerStateOnOpen();
        }
    },

    beforeUnmount() {
        clearTimeout(this._loadDebounceTimer);
        this.stopProgressPolling();
    },

    methods: {
        close() {
            this.$emit('close');
        },

        handleUpdateActive(val) {
            if (!val) {
                // stop polling before notifying parent so no final progress event races
                this.stopProgressPolling();
                this.$emit('close');
            }
        },
        initializeSelectedSco() {
            // Priority: itemId prop > first SCO > null
            if (this.itemId) {
                this.selectedScoId = this.itemId;
            } else if (this.availableScos.length > 0) {
                this.selectedScoId = this.availableScos[0].id;
            } else {
                this.selectedScoId = null;
            }
        },

        onScoChange() {
            if (this.active) {
                this.debounceLoadPreview();
            }
        },

        debounceLoadPreview() {
            clearTimeout(this._loadDebounceTimer);
            this._loadDebounceTimer = setTimeout(() => {
                this.loadPreview();
            }, LOAD_DEBOUNCE_MS);
        },

        isValidUrl(url) {
            try {
                const parsed = new URL(url, window.location.origin);
                return parsed.protocol === 'http:' || parsed.protocol === 'https:';
            } catch {
                return false;
            }
        },

        async loadPreview() {
            if (!this.active || !this.packageId) {
                return;
            }

            const token = ++this.loadToken;

            this.previewError = null;
            this.launchUrl = null;
            this.iframeReady = false;
            this.previewLoading = true;

            try {
                const data = this.activityId
                    ? await fetchPackageLaunch(this.activityId, this.selectedScoId)
                    : await fetchPackagePreview(this.packageId, this.selectedScoId);

                if (token !== this.loadToken) {
                    return;
                }

                this.attemptId = data.attempt_id ?? null;
                this.launchUrl = data.launch_url ?? null;

                if (!this.launchUrl) {
                    this.previewError = 'Preview URL was not returned for this package.';
                    return;
                }

                if (!this.isValidUrl(this.launchUrl)) {
                    this.previewError = 'Invalid preview URL received from server.';
                    this.launchUrl = null;
                    return;
                }

                this.iframeKey += 1;
            } catch (error) {
                if (token !== this.loadToken) {
                    return;
                }

                this.previewError = error?.response?.data?.message || 'This package is not ready to preview yet.';
            } finally {
                if (token === this.loadToken) {
                    this.previewLoading = false;
                }
            }
        },

        resetPreview() {
            this.loadToken += 1;
            this.previewLoading = false;
            this.previewError = null;
            this.launchUrl = null;
            this.iframeReady = false;
            this.attemptId = null;
        },

        onIframeLoad() {
            this.iframeReady = true;
        },

        startProgressPolling() {
            if (!this.activityId) {
                return;
            }

            this.stopProgressPolling();
            this._progressPollTimer = setInterval(() => {
                this.$emit('progress-synced');
            }, 15000);
        },

        stopProgressPolling() {
            clearInterval(this._progressPollTimer);
            this._progressPollTimer = null;
        },

        async resetAttempt() {
            this.resettingAttempt = true;
            try {
                await this.loadPreview();
            } finally {
                this.resettingAttempt = false;
            }
        },

        handleRestore(payload) {
            if (!this.rememberDrawerState || payload?.relaunch === false) {
                return;
            }

            if (this.active) {
                this.debounceLoadPreview();
            }
        },

        syncDrawerStateOnOpen() {
            if (!this.rememberDrawerState) {
                this.isDrawerMaximized = false;
                return;
            }

            this.loadSavedDrawerState();
        },

        loadSavedDrawerState() {
            try {
                const setting = UserSetting.findByKey('elearning_package_drawer_state');
                const savedState = setting?.value;

                if (savedState === 'fullscreen') {
                    this.isDrawerMaximized = true;
                } else {
                    this.isDrawerMaximized = false;
                }
            } catch {
                this.$edverumToast?.open({
                    message: 'Failed to load eLearning package drawer state',
                    type: 'is-warning',
                });
                this.isDrawerMaximized = false;
            }
        },

        async handleMaximizedChange(isMaximized) {
            this.isDrawerMaximized = isMaximized;

            if (!this.rememberDrawerState) {
                return;
            }

            await this.saveDrawerState(isMaximized);
        },

        async saveDrawerState(isMaximized) {
            if (!this.rememberDrawerState) {
                return;
            }

            try {
                const setting = UserSetting.findByKey('elearning_package_drawer_state');
                const state = isMaximized ? 'fullscreen' : 'normal';
                await setting.set(state);
            } catch {
                this.$edverumToast?.open({
                    message: 'Failed to save eLearning package drawer state',
                    type: 'is-warning',
                });
            }
        },
    },
};
</script>

<style scoped>
/* ── Drawer root (fill sidebar; player gets full height immediately) ── */
.scorm-drawer {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    height: 100%;
    overflow: hidden;
}

/* ── Sub-header ── */
.scorm-subheader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 0 12px;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 0;
    flex-shrink: 0;
}

.scorm-subheader__left,
.scorm-subheader__right {
    display: flex;
    align-items: center;
    gap: 8px;
}

.scorm-badge {
    display: inline-block;
    padding: 2px 9px;
    border-radius: 4px;
    font-weight: 600;
}

.scorm-badge--standard {
    background: #2563eb;
    color: #fff;
}

.scorm-badge--version {
    border: 1px solid #2563eb;
    color: #2563eb;
    background: transparent;
}

.scorm-ctrl-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: transparent;
    border: 1px solid #e5e7eb;
    color: #374151;
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 5px;
    transition: background 0.15s;
}

.scorm-ctrl-btn:hover:not(:disabled) {
    background: #f3f4f6;
}

.scorm-ctrl-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.scorm-ctrl-btn--icon {
    padding: 4px 8px;
}

.scorm-ctrl-btn--active {
    background: #eff6ff;
    border-color: #93c5fd;
    color: #1d4ed8;
}

/* SCO selector */
.scorm-sco-selector {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-left: 12px;
}

.scorm-sco-selector__label {
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
}

.scorm-sco-selector__select {
    padding: 4px 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    color: #374151;
    background: #fff;
    cursor: pointer;
    max-width: 240px;
    transition: border-color 0.15s;
}

.scorm-sco-selector__select:hover {
    border-color: #9ca3af;
}

.scorm-sco-selector__select:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

/* ── Split body (full height immediately; no layout transitions) ── */
.scorm-body {
    display: flex;
    flex: 1;
    min-height: 0;
    height: 100%;
    overflow: hidden;
}

/* ── Player pane ── */
.scorm-player {
    flex: 1;
    min-width: 0;
    min-height: 0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f9fafb;
    border-right: 1px solid #e5e7eb;
    position: relative;
    overflow: hidden;
}

.scorm-player__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 24px;
    text-align: center;
}

.scorm-player__error {
    max-width: 360px;
}

.scorm-retry-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: #2563eb;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
}

.scorm-retry-btn:hover {
    background: #1d4ed8;
}

.scorm-iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: none;
    opacity: 0;
    transition: opacity 0.2s;
}

.scorm-iframe--ready {
    opacity: 1;
}

/* ── Runtime panel ── */
.scorm-runtime {
    width: 340px; /* RUNTIME_PANEL_WIDTH_PX constant */
    flex-shrink: 0;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: #f3f4f6;
    overflow: hidden;
    border-left: 1px solid #e5e7eb;
}

.scorm-runtime__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-bottom: 1px solid #e5e7eb;
}

.scorm-runtime__title {
    font-weight: 700;
    letter-spacing: 0.08em;
    color: #6b7280;
    text-transform: uppercase;
}

.scorm-runtime__hide {
    background: transparent;
    border: none;
    color: #3b82f6;
    cursor: pointer;
}

/* When API log is hidden, show a small control to reopen it */
.scorm-runtime__show {
    background: transparent;
    border: none;
    color: #374151;
    cursor: pointer;
}

.scorm-runtime__hide:hover {
    text-decoration: underline;
}

.scorm-runtime__section {
    padding: 12px 14px;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
}

.scorm-runtime__section--cmi {
    flex-shrink: 0;
}

.scorm-runtime__section--log {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-bottom: none;
}

.scorm-runtime__section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
    flex-shrink: 0;
}

.scorm-runtime__section-label {
    font-weight: 600;
    color: #374151;
    margin-bottom: 0;
}

.scorm-runtime__section--cmi .scorm-runtime__section-label {
    margin-bottom: 8px;
}

/* CMI table */
.cmi-table {
    width: 100%;
    border-collapse: collapse;
    font-family: 'Courier New', Courier, monospace;
}

.cmi-table__key {
    color: #6b7280;
    padding: 2px 0;
    width: 60%;
}

.cmi-table__value {
    color: #111827;
    font-weight: 500;
    text-align: right;
}

.cmi-table__value--failed {
    color: #dc2626;
}
.cmi-table__value--passed {
    color: #16a34a;
}

/* API log */
.api-log {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    font-family: 'Courier New', Courier, monospace;
    line-height: 1.6;
}

.api-log__entry {
    padding: 3px 0;
    border-bottom: 1px solid #e5e7eb;
    color: #374151;
    word-break: break-all;
}

.api-log__entry:last-child {
    border-bottom: none;
}

.decline-report-close-btn {
    min-width: 5.5rem;
    border: 1.5px solid #2b80d6;
    color: #343638;
    border-radius: 8px;
    padding: 0.45rem 0.85rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
}

.decline-report-close-btn i {
    color: inherit;
}

.decline-report-close-btn:hover {
    background: rgba(43, 128, 214, 0.06);
    border-color: #1f6fb8;
}
</style>
